import { useState, useRef, useEffect } from 'react';

interface SelectFieldProps {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function SelectField({ label, options, value, onChange, placeholder = 'Select...' }: SelectFieldProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-mono uppercase tracking-wider text-text-muted mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full h-12 px-4 flex items-center justify-between bg-bg-elevated border rounded-md font-mono text-sm transition-all duration-200 cursor-pointer ${
          open ? 'border-accent text-text-primary' : 'border-border-muted text-text-secondary hover:border-border-active'
        }`}
      >
        <span className={value ? 'text-text-primary' : 'text-text-muted'}>{value || placeholder}</span>
        <span className="w-4 h-4 flex items-center justify-center text-text-muted">
          {open ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
        </span>
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-bg-panel border border-border-muted rounded-md shadow-xl overflow-hidden animate-fade-in">
          <div className="max-h-60 overflow-y-auto">
            {options.map((opt) => (
              <button key={opt} type="button" onClick={() => { onChange(opt); setOpen(false); }}
                className={`w-full px-4 py-2.5 text-left font-mono text-sm transition-colors cursor-pointer ${
                  value === opt ? 'bg-accent/20 text-accent' : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                }`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface AnswerControlsProps {
  classification: string; severity: string; nextAction: string;
  onClassificationChange: (v: string) => void; onSeverityChange: (v: string) => void; onNextActionChange: (v: string) => void;
  onSubmit: () => void; classifications: string[]; severities: string[]; actions: string[];
}

export default function AnswerControls({ classification, severity, nextAction, onClassificationChange, onSeverityChange, onNextActionChange, onSubmit, classifications, severities, actions }: AnswerControlsProps) {
  const allSelected = classification && severity && nextAction;
  return (
    <div className="bg-bg-panel border border-border-subtle rounded-xl p-5 md:p-6">
      <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-5">Your Judgement</h3>
      <div className="space-y-5">
        <SelectField label="Failure Classification" options={classifications} value={classification} onChange={onClassificationChange} placeholder="Select failure type..." />
        <SelectField label="Severity" options={severities} value={severity} onChange={onSeverityChange} placeholder="Select severity level..." />
        <SelectField label="Next Action" options={actions} value={nextAction} onChange={onNextActionChange} placeholder="Select best next action..." />
        <button type="button" onClick={onSubmit} disabled={!allSelected}
          className={`w-full h-14 rounded-md font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-2 ${
            allSelected ? 'bg-accent hover:bg-accent-hover text-bg-base' : 'bg-bg-elevated text-text-muted cursor-not-allowed border border-border-subtle'
          }`}>
          <span className="w-5 h-5 flex items-center justify-center"><i className="ri-check-double-line" /></span>
          Check my judgement
        </button>
        {!allSelected && <p className="text-xs text-text-muted text-center font-mono">Select all three fields to submit</p>}
      </div>
    </div>
  );
}
